'use client';

import { Info } from 'lucide-react';

import { useMemo } from 'react';

import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';

import { METRIC_HELPER_TEXTS } from '@/features/analytics/constants/analytics-constants';
import { formatHours, formatNumber } from '@/features/analytics/utils/analytics-utils';

import { Tooltip } from '@/shared/components';

import { cn } from '@/lib/utils';

export type FunnelStage = {
	name: string;
	count: number;
	timeHours?: number | null;
};

type CandidateFunnelProps = {
	stages: FunnelStage[];
	showTimeData?: boolean;
	className?: string;
	useDummyData?: boolean;
};

const DUMMY_FUNNEL_STAGES: FunnelStage[] = [
	{ name: 'New', count: 100 },
	{ name: 'Shortlisted', count: 15 },
	{ name: 'Archived', count: 99 },
	{ name: 'Unknown', count: 53 },
	{ name: 'Other', count: 7 },
];

/**
 * Reusable candidate funnel component showing stage distribution
 * Used in form analytics modal and form summary cards
 */
export function CandidateFunnel({
	stages,
	showTimeData = false,
	className,
	useDummyData = false,
}: CandidateFunnelProps) {
	const displayStages = useMemo(() => {
		if (useDummyData) return DUMMY_FUNNEL_STAGES;
		return stages;
	}, [useDummyData, stages]);

	// Check if any stage has time data (for showing '-' placeholder)
	const hasAnyTimeData = useMemo(() => {
		if (!showTimeData) return false;
		return displayStages.some(
			(s) => s.timeHours !== null && s.timeHours !== undefined && s.timeHours > 0
		);
	}, [displayStages, showTimeData]);

	if (!displayStages.length) return null;

	return (
		<ScrollArea className={className}>
			<div className="flex">
				{displayStages.map((stage, index) => {
					const hasTimeData =
						showTimeData &&
						stage.timeHours !== null &&
						stage.timeHours !== undefined &&
						stage.timeHours > 0;

					return (
						<div
							key={stage.name}
							className={cn(
								'flex w-[90px] flex-col items-center border border-border bg-muted/30 px-2 py-1.5 sm:w-[100px] sm:px-3 sm:py-2',
								index === 0 && 'rounded-l-md',
								index === displayStages.length - 1 && 'rounded-r-md',
								index > 0 && '-ml-px'
							)}
						>
							<p className="text-base text-secondary sm:text-lg">{formatNumber(stage.count)}</p>
							<p className="max-w-[80px] truncate text-[10px] text-secondary sm:max-w-[90px] sm:text-xs">
								{stage.name}
							</p>
							{hasTimeData ? (
								<div className="mt-1 flex items-center gap-1">
									<p className="text-[10px] text-secondary">{formatHours(stage.timeHours)}</p>
									<Tooltip text={METRIC_HELPER_TEXTS.avgTimeToReachStage}>
										<Info className="h-2.5 w-2.5 cursor-default text-muted-foreground/70 transition-colors hover:text-muted-foreground" />
									</Tooltip>
								</div>
							) : hasAnyTimeData ? (
								<p className="mt-1 text-[10px] text-secondary">-</p>
							) : null}
						</div>
					);
				})}
			</div>
			<ScrollBar orientation="horizontal" />
		</ScrollArea>
	);
}
